<template>
  <HrPageLayout
    title="Employee Promotions"
    subtitle="Manage employee promotions and career progression"
    icon="📈"
    :breadcrumb-items="[{ label: 'HR Management' }, { label: 'Promotions' }]"
  >
    <template #header-actions>
      <button @click="createPromotion" class="hr-btn hr-btn-primary">
        <i class="mdi mdi-plus"></i> Create Promotion
      </button>
      <button @click="refreshData" class="hr-btn hr-btn-secondary">
        <i class="mdi mdi-refresh"></i> Refresh
      </button>
      <button @click="exportPromotionsCsv" class="hr-btn hr-btn-success">
        <i class="mdi mdi-file-export"></i> CSV
      </button>
      <button @click="exportPromotionsPdf" class="hr-btn hr-btn-outline-success">
        <i class="mdi mdi-file-pdf-box"></i> PDF
      </button>
    </template>

    <div class="container-fluid">
      <!-- Filters and controls section -->
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h5 class="card-title mb-3">Promotion Filters</h5>
              <div class="row g-3">
                <div class="col-md-3">
                  <label class="form-label">Promotion Type</label>
                  <select v-model="filters.promotionType" class="form-select">
                    <option value="">All Promotion Types</option>
                    <option v-for="type in promotionTypes" :key="type" :value="type">
                      {{ type }}
                    </option>
                  </select>
                </div>

                <div class="col-md-3">
                  <label class="form-label">Department</label>
                  <select v-model="filters.departmentId" class="form-select">
                    <option value="">All Departments</option>
                    <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                      {{ dept.departmentName }}
                    </option>
                  </select>
                </div>

                <div class="col-md-3">
                  <label class="form-label">Date Range</label>
                  <div class="input-group">
                    <input v-model="filters.startDate" type="date" class="form-control">
                    <span class="input-group-text">to</span>
                    <input v-model="filters.endDate" type="date" class="form-control">
                  </div>
                </div>

                <div class="col-md-3">
                  <label class="form-label">Search</label>
                  <div class="position-relative">
                    <input
                      type="text"
                      v-model="filters.search"
                      class="form-control"
                      placeholder="Search by name, designation..."
                    >
                    <i class="bx bx-search-alt search-icon position-absolute top-50 end-0 translate-middle-y me-2"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Statistics cards -->
      <div class="row">
        <!-- Total Promotions -->
        <div class="col-xl-3 col-md-6 mb-4">
          <div class="card border-left-primary h-100 py-2">
            <div class="card-body">
              <div class="d-flex align-items-center">
                <div class="flex-shrink-0 me-3">
                  <div class="avatar-sm">
                    <span class="avatar-title bg-primary rounded-circle text-white font-size-20">
                      <i class="mdi mdi-briefcase-upload font-size-24"></i>
                    </span>
                  </div>
                </div>
                <div class="flex-grow-1">
                  <h6 class="font-size-15 mb-1">Total Promotions</h6>
                  <h4 class="mb-0">{{ metrics.totalPromotions || 0 }}</h4>
                  <span class="text-muted font-size-12">All time</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pending Promotions -->
        <div class="col-xl-3 col-md-6 mb-4">
          <div class="card border-left-warning h-100 py-2">
            <div class="card-body">
              <div class="d-flex align-items-center">
                <div class="flex-shrink-0 me-3">
                  <div class="avatar-sm">
                    <span class="avatar-title bg-warning rounded-circle text-white font-size-20">
                      <i class="mdi mdi-clock-outline font-size-24"></i>
                    </span>
                  </div>
                </div>
                <div class="flex-grow-1">
                  <h6 class="font-size-15 mb-1">Pending Approvals</h6>
                  <h4 class="mb-0">{{ metrics.pendingPromotions || 0 }}</h4>
                  <span class="text-warning font-size-12">Awaiting review</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Approved Promotions -->
        <div class="col-xl-3 col-md-6 mb-4">
          <div class="card border-left-success h-100 py-2">
            <div class="card-body">
              <div class="d-flex align-items-center">
                <div class="flex-shrink-0 me-3">
                  <div class="avatar-sm">
                    <span class="avatar-title bg-success rounded-circle text-white font-size-20">
                      <i class="mdi mdi-check-circle font-size-24"></i>
                    </span>
                  </div>
                </div>
                <div class="flex-grow-1">
                  <h6 class="font-size-15 mb-1">Approved</h6>
                  <h4 class="mb-0">{{ metrics.approvedPromotions || 0 }}</h4>
                  <span class="text-success font-size-12">Ready to process</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Upcoming Promotions -->
        <div class="col-xl-3 col-md-6 mb-4">
          <div class="card border-left-info h-100 py-2">
            <div class="card-body">
              <div class="d-flex align-items-center">
                <div class="flex-shrink-0 me-3">
                  <div class="avatar-sm">
                    <span class="avatar-title bg-info rounded-circle text-white font-size-20">
                      <i class="mdi mdi-calendar-clock font-size-24"></i>
                    </span>
                  </div>
                </div>
                <div class="flex-grow-1">
                  <h6 class="font-size-15 mb-1">Upcoming</h6>
                  <h4 class="mb-0">{{ metrics.upcomingPromotions || 0 }}</h4>
                  <span class="text-info font-size-12">Next 30 days</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Promotion Type Breakdown and Upcoming Promotions -->
      <div class="row">
        <!-- Promotion Type Breakdown -->
        <div class="col-xl-6 col-lg-6 mb-4">
          <div class="card h-100">
            <div class="card-header bg-transparent">
              <h5 class="card-title mb-0">Promotions by Type</h5>
            </div>
            <div class="card-body">
              <div v-if="metrics.typeCounts && Object.keys(metrics.typeCounts).length > 0" class="chart-container" style="position: relative; height:250px; width:100%">
                <div class="table-responsive">
                  <table class="table table-sm table-bordered">
                    <thead class="table-light">
                      <tr>
                        <th>Promotion Type</th>
                        <th class="text-center">Count</th>
                        <th class="text-center">Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(count, type) in metrics.typeCounts" :key="type">
                        <td>{{ type }}</td>
                        <td class="text-center">{{ count }}</td>
                        <td class="text-center">
                          <div class="progress" style="height: 6px;">
                            <div
                              class="progress-bar"
                              :class="getPromotionTypeColor(type)"
                              role="progressbar"
                              :style="`width: ${getPromotionTypePercentage(type)}%`"
                              :aria-valuenow="getPromotionTypePercentage(type)"
                              aria-valuemin="0"
                              aria-valuemax="100"
                            ></div>
                          </div>
                          {{ getPromotionTypePercentage(type) }}%
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-else class="text-center p-4">
                <i class="mdi mdi-chart-bar font-size-48 text-muted"></i>
                <p class="mt-2">No promotion type data available</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Upcoming Promotions List -->
        <div class="col-xl-6 col-lg-6 mb-4">
          <div class="card h-100">
            <div class="card-header bg-transparent">
              <h5 class="card-title mb-0">Upcoming Promotions</h5>
            </div>
            <div class="card-body">
              <div v-if="upcomingPromotions.length > 0" class="upcoming-promotions-list">
                <div v-for="promotion in upcomingPromotions" :key="promotion.id" class="upcoming-promotion-item mb-3">
                  <div class="d-flex align-items-center p-3 border rounded">
                    <div class="promotion-date-badge me-3 text-center">
                      <span class="date-month">{{ formatDateMonth(promotion.effectiveDate) }}</span>
                      <span class="date-day fs-4 fw-bold d-block">{{ formatDateDay(promotion.effectiveDate) }}</span>
                    </div>
                    <div class="promotion-details flex-grow-1">
                      <h6 class="promotion-title mb-1">{{ promotion.employeeName }}</h6>
                      <div class="promotion-meta text-muted small">
                        <span><i class="mdi mdi-briefcase-outline me-1"></i>{{ promotion.previousDesignation }} → {{ promotion.newDesignation }}</span>
                        <span class="ms-2"><i class="mdi mdi-office-building me-1"></i>{{ promotion.newDepartmentName || 'N/A' }}</span>
                      </div>
                    </div>
                    <div class="promotion-type-badge ms-2">
                      <span class="badge rounded-pill" :class="getPromotionTypeColor(promotion.promotionType)">{{ promotion.promotionType }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-center p-4">
                <i class="mdi mdi-calendar-check font-size-48 text-muted"></i>
                <p class="mt-2">No upcoming promotions</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Promotions List Table -->
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-header bg-transparent">
              <h5 class="card-title">Promotions List</h5>
            </div>
            <div class="card-body">
              <!-- Loading overlay -->
              <div v-if="loading" class="text-center p-5">
                <div class="spinner-border text-primary me-2" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-2">Loading promotions data...</p>
              </div>

              <!-- Empty state -->
              <div v-else-if="!promotions.length" class="text-center p-5">
                <i class="mdi mdi-briefcase-remove font-size-48 text-muted"></i>
                <h5 class="mt-2">No promotions found</h5>
                <p class="text-muted">Try adjusting your filters or create a new promotion</p>
                <button @click="resetFilters" class="btn btn-sm btn-outline-primary mt-2">Reset Filters</button>
              </div>

              <!-- Promotions table -->
              <div v-else class="table-responsive">
                <table class="table table-hover table-bordered align-middle mb-0">
                  <thead class="table-light">
                    <tr>
                      <th>Employee</th>
                      <th>Promotion Details</th>
                      <th>Dates</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th class="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="promotion in promotions" :key="promotion.id">
                      <td>
                        <div class="d-flex align-items-center">
                          <div class="avatar-xs me-2 bg-light rounded p-1 text-center">
                            <i class="mdi mdi-account font-size-18 text-primary"></i>
                          </div>
                          <div>
                            <h5 class="font-size-14 mb-0">{{ promotion.employeeName }}</h5>
                            <p class="text-muted mb-0 font-size-12">{{ promotion.promotionCode }}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div class="d-flex flex-column">
                          <div>
                            <span class="badge bg-light text-dark me-1">From:</span>
                            <span>{{ promotion.previousDesignation }}</span>
                          </div>
                          <div class="mt-1">
                            <span class="badge bg-light text-dark me-1">To:</span>
                            <span>{{ promotion.newDesignation }}</span>
                          </div>
                          <div class="mt-1" v-if="promotion.previousDepartmentName !== promotion.newDepartmentName">
                            <span class="badge bg-light text-dark me-1">Dept Change:</span>
                            <span>{{ promotion.previousDepartmentName }} → {{ promotion.newDepartmentName }}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <span class="d-block"><i class="mdi mdi-calendar-plus me-1"></i>Created: {{ formatDate(promotion.promotionDate) }}</span>
                          <span class="d-block mt-1"><i class="mdi mdi-calendar-clock me-1"></i>Effective: {{ formatDate(promotion.effectiveDate) }}</span>
                        </div>
                      </td>
                      <td>
                        <span class="badge rounded-pill" :class="getPromotionTypeColor(promotion.promotionType)">{{ promotion.promotionType }}</span>
                      </td>
                      <td>
                        <span class="badge" :class="getStatusBadgeClass(promotion.status)">
                          {{ promotion.status }}
                        </span>
                      </td>
                      <td class="text-center">
                        <div class="d-flex justify-content-center">
                          <button
                            @click="viewPromotionDetails(promotion)"
                            class="btn btn-sm btn-info me-2"
                            title="View Details"
                          >
                            <i class="mdi mdi-eye"></i>
                          </button>
                          <button
                            v-if="promotion.status === 'Pending'"
                            @click="editPromotion(promotion)"
                            class="btn btn-sm btn-primary me-2"
                            title="Edit Promotion"
                          >
                            <i class="mdi mdi-pencil"></i>
                          </button>
                          <button
                            v-if="canApprove(promotion)"
                            @click="approvePromotion(promotion)"
                            class="btn btn-sm btn-success me-2"
                            title="Approve Promotion"
                          >
                            <i class="mdi mdi-check"></i>
                          </button>
                          <button
                            v-if="promotion.status === 'Pending'"
                            @click="rejectPromotion(promotion)"
                            class="btn btn-sm btn-danger"
                            title="Reject Promotion"
                          >
                            <i class="mdi mdi-close"></i>
                          </button>
                          <button
                            v-if="promotion.status === 'Approved'"
                            @click="completePromotion(promotion)"
                            class="btn btn-sm btn-success"
                            title="Complete Promotion"
                          >
                            <i class="mdi mdi-check-all"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Pagination -->
              <div v-if="promotions.length" class="d-flex justify-content-between align-items-center mt-4">
                <div>
                  Showing <span class="fw-medium">{{ promotions.length }}</span> of
                  <span class="fw-medium">{{ pagination.totalPromotions }}</span> promotions
                </div>
                <nav v-if="pagination.totalPages > 1">
                  <ul class="pagination pagination-rounded mb-0">
                    <li class="page-item" :class="{ disabled: pagination.currentPage <= 1 }">
                      <a class="page-link" href="javascript:void(0)" @click="changePage(pagination.currentPage - 1)">
                        <i class="mdi mdi-chevron-left"></i>
                      </a>
                    </li>
                    <li
                      v-for="page in getPageNumbers()"
                      :key="page"
                      class="page-item"
                      :class="{ active: page === pagination.currentPage, disabled: page === '...' }"
                    >
                      <a v-if="page !== '...'" class="page-link" href="javascript:void(0)" @click="changePage(page)">
                        {{ page }}
                      </a>
                      <span v-else class="page-link">{{ page }}</span>
                    </li>
                    <li class="page-item" :class="{ disabled: pagination.currentPage >= pagination.totalPages }">
                      <a class="page-link" href="javascript:void(0)" @click="changePage(pagination.currentPage + 1)">
                        <i class="mdi mdi-chevron-right"></i>
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Promotion Create/Edit Modal (Vue-based) -->
    <div v-if="showPromotionModal" class="modal-overlay" @click.self="closePromotionModal">
      <div class="modal-container modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ isEditMode ? 'Edit Promotion' : 'Create New Promotion' }}</h5>
            <button type="button" class="btn-close" @click="closePromotionModal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitPromotion">
              <div class="row g-3">
                <!-- Employee Selection (when creating) -->
                <div v-if="!isEditMode" class="col-md-12">
                  <label class="form-label">Select Employee <span class="text-danger">*</span></label>
                  <input type="text" v-model="employeeSearch" class="form-control mb-2" placeholder="Search employee by name, ID or designation...">
                  <select v-model="promotionForm.employeeId" class="form-select" required>
                    <option value="" disabled>Select Employee</option>
                    <option v-for="employee in filteredEligibleEmployees" :key="employee.id" :value="employee.id">
                      {{ employee.name }} - {{ employee.designation }} ({{ employee.departmentName }})
                    </option>
                  </select>
                  <small class="text-muted">
                    <i class="mdi mdi-information-outline me-1"></i>
                    Displaying matching eligible employees
                  </small>
                </div>

                <!-- Employee details (when selected or editing) -->
                <div v-if="selectedEmployee" class="col-md-12">
                  <div class="card bg-light">
                    <div class="card-body p-3">
                      <h6 class="card-title mb-2">Current Employment Details</h6>
                      <div class="row">
                        <div class="col-md-6">
                          <p class="mb-1"><strong>Name:</strong> {{ selectedEmployee.name }}</p>
                          <p class="mb-1"><strong>Designation:</strong> {{ selectedEmployee.designation }}</p>
                          <p class="mb-1"><strong>Department:</strong> {{ selectedEmployee.departmentName }}</p>
                        </div>
                        <div class="col-md-6">
                          <p class="mb-1"><strong>Years of Service:</strong> {{ selectedEmployee.serviceYears || calculateServiceYears(selectedEmployee.hireDate) }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <label class="form-label">Current Designation</label>
                  <input
                    v-model="promotionForm.previousDesignation"
                    type="text"
                    class="form-control"
                    readonly
                  >
                </div>

                <div class="col-md-6">
                  <label class="form-label">New Designation <span class="text-danger">*</span></label>
                  <select v-model="promotionForm.newDesignation" class="form-select" required>
                    <option value="" disabled>Select Designation</option>
                    <option v-for="pos in positions" :key="pos.id" :value="pos.positionName">
                      {{ pos.positionName }} ({{ pos.code }})
                    </option>
                  </select>
                </div>

                <div class="col-md-6">
                  <label class="form-label">Current Department</label>
                  <input
                    v-model="promotionForm.previousDepartmentName"
                    type="text"
                    class="form-control"
                    readonly
                  >
                  <input type="hidden" v-model="promotionForm.previousDepartmentId">
                </div>

                <div class="col-md-6">
                  <label class="form-label">New Department</label>
                  <select v-model="promotionForm.newDepartmentId" class="form-select">
                    <option :value="promotionForm.previousDepartmentId">{{ promotionForm.previousDepartmentName }} (Same)</option>
                    <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                      {{ dept.departmentName }}
                    </option>
                  </select>
                </div>



                <div class="col-md-6">
                  <label class="form-label">Promotion Date <span class="text-danger">*</span></label>
                  <input v-model="promotionForm.promotionDate" type="date" class="form-control" required>
                </div>

                <div class="col-md-6">
                  <label class="form-label">Effective Date <span class="text-danger">*</span></label>
                  <input v-model="promotionForm.effectiveDate" type="date" class="form-control" required>
                </div>

                <div class="col-md-6">
                  <label class="form-label">Promotion Type <span class="text-danger">*</span></label>
                  <select v-model="promotionForm.promotionType" class="form-select" required>
                    <option value="" disabled>Select Promotion Type</option>
                    <option v-for="type in promotionTypes" :key="type" :value="type">{{ type }}</option>
                  </select>
                </div>

                <div class="col-md-6">
                  <label class="form-label">Performance Rating</label>
                  <select v-model="promotionForm.performanceRating" class="form-select">
                    <option value="">Not Specified</option>
                    <option v-for="rating in [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5]" :key="rating" :value="rating">
                      {{ rating }} {{ rating >= 4 ? '(Excellent)' : rating >= 3 ? '(Good)' : rating >= 2 ? '(Average)' : '(Poor)' }}
                    </option>
                  </select>
                </div>

                <div class="col-md-12">
                  <label class="form-label">Reason for Promotion <span class="text-danger">*</span></label>
                  <textarea v-model="promotionForm.reason" class="form-control" rows="3" required></textarea>
                </div>

                <div class="col-md-12">
                  <label class="form-label">Recommendations</label>
                  <textarea v-model="promotionForm.recommendations" class="form-control" rows="2"></textarea>
                </div>

                <div class="col-md-12">
                  <label class="form-label">Supporting Documents</label>
                  <input
                    type="file"
                    class="form-control"
                    multiple
                    ref="documentFiles"
                    @change="handleFileSelection"
                  >
                  <small class="text-muted">Performance reviews, certificates, approval emails, etc.</small>
                </div>

                <!-- Display existing documents when editing -->
                <div v-if="promotionForm.documentsList && promotionForm.documentsList.length > 0" class="col-md-12">
                  <label class="form-label">Existing Documents</label>
                  <div class="list-group">
                    <div v-for="(doc, index) in promotionForm.documentsList" :key="index" class="list-group-item list-group-item-action d-flex justify-content-between align-items-center">
                      <div>
                        <i class="mdi mdi-file-document me-2"></i>
                        {{ doc.name }}
                      </div>
                      <button
                        type="button"
                        class="btn btn-sm btn-danger"
                        @click="removeDocument(doc, index)"
                      >
                        <i class="mdi mdi-delete"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closePromotionModal">Close</button>
            <button type="button" class="btn btn-primary" @click="submitPromotion">
              {{ isEditMode ? 'Update Promotion' : 'Create Promotion' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Promotion Details Modal (Vue-based) -->
    <div v-if="showDetailsModal" class="modal-overlay" @click.self="closeDetailsModal">
      <div class="modal-container modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Promotion Details</h5>
            <button type="button" class="btn-close" @click="closeDetailsModal"></button>
          </div>
          <div class="modal-body">
            <div v-if="selectedPromotion">
              <div class="row mb-4">
                <div class="col-md-8">
                  <h4 class="font-size-16">{{ selectedPromotion.employeeName }}</h4>
                  <p class="text-muted mb-1">
                    <span class="badge rounded-pill" :class="getPromotionTypeColor(selectedPromotion.promotionType)">
                      {{ selectedPromotion.promotionType }}
                    </span>
                  </p>
                  <p class="text-muted mb-0">Promotion Code: {{ selectedPromotion.promotionCode }}</p>
                </div>
                <div class="col-md-4 text-md-end">
                  <span class="badge" :class="getStatusBadgeClass(selectedPromotion.status)">
                    {{ selectedPromotion.status }}
                  </span>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="card border">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Promotion Information</h6>
                    </div>
                    <div class="card-body">
                      <ul class="list-group list-group-flush">
                        <li class="list-group-item">
                          <div class="d-flex justify-content-between">
                            <span><i class="mdi mdi-calendar me-2"></i>Promotion Date:</span>
                            <span class="text-muted">{{ formatDate(selectedPromotion.promotionDate) }}</span>
                          </div>
                        </li>
                        <li class="list-group-item">
                          <div class="d-flex justify-content-between">
                            <span><i class="mdi mdi-calendar-clock me-2"></i>Effective Date:</span>
                            <span class="text-muted">{{ formatDate(selectedPromotion.effectiveDate) }}</span>
                          </div>
                        </li>
                        <li class="list-group-item">
                          <div class="d-flex justify-content-between">
                            <span><i class="mdi mdi-briefcase-outline me-2"></i>Previous Designation:</span>
                            <span class="text-muted">{{ selectedPromotion.previousDesignation }}</span>
                          </div>
                        </li>
                        <li class="list-group-item">
                          <div class="d-flex justify-content-between">
                            <span><i class="mdi mdi-briefcase-upload me-2"></i>New Designation:</span>
                            <span class="text-muted">{{ selectedPromotion.newDesignation }}</span>
                          </div>
                        </li>
                        <li class="list-group-item" v-if="selectedPromotion.previousDepartmentName || selectedPromotion.newDepartmentName">
                          <div class="d-flex justify-content-between">
                            <span><i class="mdi mdi-office-building me-2"></i>Department:</span>
                            <span class="text-muted">
                              {{ selectedPromotion.previousDepartmentName }}
                              <i v-if="selectedPromotion.previousDepartmentName !== selectedPromotion.newDepartmentName" class="mdi mdi-arrow-right mx-1"></i>
                              {{ selectedPromotion.previousDepartmentName !== selectedPromotion.newDepartmentName ? selectedPromotion.newDepartmentName : '' }}
                            </span>
                          </div>
                        </li>
                        <li class="list-group-item" v-if="selectedPromotion.previousSalary || selectedPromotion.newSalary">
                          <div class="d-flex justify-content-between">
                            <span><i class="mdi mdi-currency-sign me-2"></i>Salary:</span>
                            <span class="text-muted">
                              <span
                                :class="['salary-value', {'blurred': !isSalaryRevealed('details-previous-salary'), 'revealed': isSalaryRevealed('details-previous-salary')}]"
                                @click="toggleSalaryVisibility('details-previous-salary')"
                              >
                                {{ formatSalary(selectedPromotion.previousSalary, 'details-previous-salary') }}
                              </span>
                              <i v-if="selectedPromotion.previousSalary && selectedPromotion.newSalary" class="mdi mdi-arrow-right mx-1"></i>
                              <span
                                v-if="selectedPromotion.newSalary"
                                :class="['salary-value', {'blurred': !isSalaryRevealed('details-new-salary'), 'revealed': isSalaryRevealed('details-new-salary')}]"
                                @click="toggleSalaryVisibility('details-new-salary')"
                              >
                                {{ formatSalary(selectedPromotion.newSalary, 'details-new-salary') }}
                              </span>
                              <span v-if="calculateSalaryIncreaseFromValues(selectedPromotion.previousSalary, selectedPromotion.newSalary) > 0" class="badge bg-success ms-1">
                                +{{ calculateSalaryIncreaseFromValues(selectedPromotion.previousSalary, selectedPromotion.newSalary) }}%
                              </span>
                            </span>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="card border h-100">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Reason & Recommendations</h6>
                    </div>
                    <div class="card-body">
                      <h6 class="font-size-14 mb-2">Reason for Promotion</h6>
                      <p v-if="selectedPromotion.reason">{{ selectedPromotion.reason }}</p>
                      <p v-else class="text-muted text-center">No reason provided</p>

                      <div v-if="selectedPromotion.recommendations" class="mt-3">
                        <h6 class="font-size-14 mb-2">Recommendations</h6>
                        <p>{{ selectedPromotion.recommendations }}</p>
                      </div>

                      <div v-if="selectedPromotion.performanceRating" class="mt-3">
                        <h6 class="font-size-14 mb-2">Performance Rating</h6>
                        <div class="d-flex align-items-center">
                          <span class="me-2">{{ selectedPromotion.performanceRating }}/5</span>
                          <div class="progress flex-grow-1" style="height: 6px;">
                            <div
                              class="progress-bar"
                              :class="getPerformanceRatingColor(selectedPromotion.performanceRating)"
                              :style="`width: ${(selectedPromotion.performanceRating / 5) * 100}%`"
                              role="progressbar"
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Approval Workflow Section -->
              <div class="row mt-4" v-if="selectedPromotion.approvals && selectedPromotion.approvals.length > 0">
                <div class="col-md-12">
                  <div class="card border">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Approval Workflow</h6>
                    </div>
                    <div class="card-body">
                      <div class="promotion-approval-timeline">
                        <div
                          v-for="(approval, index) in selectedPromotion.approvals"
                          :key="approval.id"
                          class="approval-step d-flex mb-3"
                        >
                          <div class="approval-step-icon me-3">
                            <div
                              class="rounded-circle d-flex align-items-center justify-content-center"
                              :class="getApprovalStatusClass(approval.status)"
                              style="width: 40px; height: 40px;"
                            >
                              <i
                                class="mdi"
                                :class="getApprovalStatusIcon(approval.status)"
                                style="font-size: 20px;"
                              ></i>
                            </div>
                            <div
                              v-if="index < selectedPromotion.approvals.length - 1"
                              class="approval-connector"
                              :class="getApprovalStatusClass(approval.status, true)"
                            ></div>
                          </div>
                          <div class="approval-step-content flex-grow-1">
                            <h6 class="mb-1">{{ approval.approverRole }}</h6>
                            <p class="text-muted mb-1">
                              <span
                                class="badge"
                                :class="getStatusBadgeClass(approval.status)"
                              >
                                {{ approval.status }}
                              </span>
                              <span
                                v-if="approval.approverName"
                                class="ms-2"
                              >
                                by {{ approval.approverName }}
                              </span>
                              <span
                                v-if="approval.approvedAt"
                                class="ms-2"
                              >
                                ({{ formatDateTime(approval.approvedAt) }})
                              </span>
                            </p>
                            <p v-if="approval.comments" class="mt-1 mb-0 text-muted fst-italic">
                              "{{ approval.comments }}"
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Documents Section -->
              <div class="row mt-4" v-if="selectedPromotion.documentsList && selectedPromotion.documentsList.length > 0">
                <div class="col-md-12">
                  <div class="card border">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Supporting Documents</h6>
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div
                          v-for="(doc, index) in selectedPromotion.documentsList"
                          :key="index"
                          class="col-md-4 col-sm-6 mb-3"
                        >
                          <div class="border rounded p-3 h-100 d-flex flex-column">
                            <div class="d-flex align-items-center mb-2">
                              <i
                                class="mdi mdi-file-document-outline me-2"
                                style="font-size: 24px;"
                              ></i>
                              <div class="text-truncate">{{ doc.name }}</div>
                            </div>
                            <div class="mt-auto text-end">
                              <a
                                :href="doc.path"
                                target="_blank"
                                class="btn btn-sm btn-info"
                              >
                                <i class="mdi mdi-download me-1"></i> View
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Activity History -->
              <div class="row mt-4" v-if="selectedPromotion.activities && selectedPromotion.activities.length > 0">
                <div class="col-md-12">
                  <div class="card border">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Activity History</h6>
                    </div>
                    <div class="card-body">
                      <div class="promotion-activity-timeline">
                        <div
                          v-for="(activity, index) in selectedPromotion.activities"
                          :key="index"
                          class="activity-item d-flex mb-3"
                        >
                          <div class="activity-icon me-3">
                            <div
                              class="rounded-circle d-flex align-items-center justify-content-center"
                              :class="getActivityTypeClass(activity.type)"
                              style="width: 36px; height: 36px;"
                            >
                              <i
                                class="mdi"
                                :class="getActivityTypeIcon(activity.type)"
                                style="font-size: 18px;"
                              ></i>
                            </div>
                          </div>
                          <div class="activity-content">
                            <div class="d-flex align-items-center mb-1">
                              <h6 class="mb-0 me-auto">{{ activity.title }}</h6>
                              <small class="text-muted">{{ formatDateTime(activity.timestamp) }}</small>
                            </div>
                            <p class="text-muted mb-0">{{ activity.description }}</p>
                            <small class="text-muted">By: {{ activity.user }}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeDetailsModal">Close</button>
            <div v-if="selectedPromotion">
              <!-- Show these buttons based on promotion status -->
              <button
                v-if="canApprove(selectedPromotion)"
                @click="approvePromotionFromDetails()"
                class="btn btn-success me-2"
              >
                <i class="mdi mdi-check me-1"></i> Approve
              </button>
              <button
                v-if="selectedPromotion.status === 'Pending'"
                @click="rejectPromotionFromDetails()"
                class="btn btn-danger me-2"
              >
                <i class="mdi mdi-close me-1"></i> Reject
              </button>
              <button
                v-if="selectedPromotion.status === 'Approved'"
                @click="completePromotionFromDetails()"
                class="btn btn-primary"
              >
                <i class="mdi mdi-check-all me-1"></i> Complete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Approval Modal (Vue-based) -->
    <div v-if="showApprovalModal" class="modal-overlay" @click.self="closeApprovalModal">
      <div class="modal-container">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Approve Promotion</h5>
            <button type="button" class="btn-close" @click="closeApprovalModal"></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to approve this promotion for <strong>{{ selectedPromotion?.employeeName }}</strong>?</p>

            <div class="mb-3">
              <label for="approvalLevel" class="form-label">Approval Level</label>
              <select id="approvalLevel" v-model="approvalForm.approvalLevel" class="form-select" required>
                <option v-for="approval in selectedPromotion?.approvals"
                        :key="approval.id"
                        :value="approval.approvalLevel"
                        :disabled="approval.status !== 'Pending'">
                  {{ approval.approvalLevel }} - {{ approval.approverRole }}
                  {{ approval.status !== 'Pending' ? '(' + approval.status + ')' : '' }}
                </option>
              </select>
            </div>

            <div class="mb-3">
              <label for="approvalComments" class="form-label">Comments</label>
              <textarea id="approvalComments" v-model="approvalForm.comments" class="form-control" rows="3"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeApprovalModal">Close</button>
            <button type="button" class="btn btn-success" @click="confirmApprovePromotion">
              <i class="mdi mdi-check me-1"></i> Approve
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Rejection Modal (Vue-based) -->
    <div v-if="showRejectionModal" class="modal-overlay" @click.self="closeRejectionModal">
      <div class="modal-container">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Reject Promotion</h5>
            <button type="button" class="btn-close" @click="closeRejectionModal"></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to reject this promotion for <strong>{{ selectedPromotion?.employeeName }}</strong>?</p>

            <div class="mb-3">
              <label for="rejectionReason" class="form-label">Reason for rejection <span class="text-danger">*</span></label>
              <textarea id="rejectionReason" v-model="rejectionForm.rejectionReason" class="form-control" rows="3" required></textarea>
            </div>

            <div class="mb-3" v-if="selectedPromotion?.approvals && selectedPromotion.approvals.length > 0">
              <label for="approvalLevel" class="form-label">Current Approval Level</label>
              <select id="approvalLevel" v-model="rejectionForm.approvalLevel" class="form-select">
                <option value="">Overall Rejection</option>
                <option v-for="approval in selectedPromotion.approvals"
                        :key="approval.id"
                        :value="approval.approvalLevel"
                        :disabled="approval.status !== 'Pending'">
                  {{ approval.approvalLevel }} - {{ approval.approverRole }}
                </option>
              </select>
              <small class="text-muted">
                Select a specific approval level or leave blank for overall rejection
              </small>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeRejectionModal">Close</button>
            <button type="button" class="btn btn-danger" @click="confirmRejectPromotion">
              <i class="mdi mdi-close me-1"></i> Reject Promotion
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Complete Promotion Modal (Vue-based) -->
    <div v-if="showCompleteModal" class="modal-overlay" @click.self="closeCompleteModal">
      <div class="modal-container">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Complete Promotion</h5>
            <button type="button" class="btn-close" @click="closeCompleteModal"></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-info">
              <i class="mdi mdi-information-outline me-2"></i>
              Completing this promotion will update the employee's official record with the new position, department and salary.
            </div>

            <p>Are you sure you want to complete the promotion process for <strong>{{ selectedPromotion?.employeeName }}</strong>?</p>

            <div class="mb-3">
              <label for="completionComments" class="form-label">Additional Comments</label>
              <textarea id="completionComments" v-model="completionForm.comments" class="form-control" rows="3"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeCompleteModal">Close</button>
            <button type="button" class="btn btn-primary" @click="confirmCompletePromotion">
              <i class="mdi mdi-check-all me-1"></i> Complete Promotion
            </button>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import { toast, authUrl, execute } from "@/api";
import _ from 'lodash';
import HrPageLayout from "./HrPageLayout.vue";
import "@/assets/css/hr-page-shared.css";
import { downloadCsv, downloadCountyTablePdf, periodLabelFromFilters } from "@/utils/countyReportExport";

export default {
  name: "HRPromotions",
  components: { HrPageLayout },
  data() {
    return {
      // Modal visibility flags
      showPromotionModal: false,
      showDetailsModal: false,
      showApprovalModal: false,
      showRejectionModal: false,
      showCompleteModal: false,
      
      submitLoading: false,
      loading: false,
      promotions: [],
      upcomingPromotions: [],
      departments: [],
      positions: [],
      employees: [],
      eligibleEmployees: [],
      employeeSearch: "",
      promotionTypes: ['Regular', 'Performance-Based', 'Probation Completion', 'Exceptional', 'Internal Transfer', 'Restructuring', 'Project Completion', 'Skills Upgrade', 'Other'],
      filters: {
        departmentId: "",
        promotionType: "",
        startDate: this.getDateOffset(-90),
        endDate: this.getDateOffset(90),
        search: "",
        page: 1,
        limit: 10
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalPromotions: 0,
        limit: 10
      },
      metrics: {
        totalPromotions: 0,
        pendingPromotions: 0,
        approvedPromotions: 0,
        rejectedPromotions: 0,
        upcomingPromotions: 0,
        typeCounts: {}
      },
      selectedPromotion: null,
      promotionForm: {
        employeeId: "",
        previousDesignation: "",
        newDesignation: "",
        previousDepartmentId: "",
        previousDepartmentName: "",
        newDepartmentId: "",
        promotionDate: new Date().toISOString().split('T')[0],
        effectiveDate: new Date().toISOString().split('T')[0],
        reason: "",
        recommendations: "",
        promotionType: "Regular",
        performanceRating: "",
        documentsList: []
      },
      isEditMode: false,
      selectedEmployee: null,
      removedDocuments: [],
      pendingFiles: [],
      approvalForm: {
        approvalLevel: "",
        comments: ""
      },
      rejectionForm: {
        rejectionReason: "",
        approvalLevel: ""
      },
      completionForm: {
        comments: ""
      }
    };
  },
  mounted() {
    this.getDepartments();
    this.getPromotions();
    this.getPositions();
  },
  computed: {
    filteredEligibleEmployees() {
      if (!this.employeeSearch) return this.eligibleEmployees;
      const s = this.employeeSearch.toLowerCase();
      return this.eligibleEmployees.filter(e => 
        (e.name && e.name.toLowerCase().includes(s)) || 
        (e.employeeNo && e.employeeNo.toLowerCase().includes(s)) ||
        (e.designation && e.designation.toLowerCase().includes(s))
      );
    }
  },
  methods: {
    getDateOffset(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toISOString().split('T')[0];
    },

    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.departments = res.data.data.departments || [];
            } else {
              console.error("Error fetching departments:", res.data.message);
              this.showToast("error", "Failed to load departments");
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching departments");
          });
    },

    getPositions() {
      const data = new FormData();
      data.append("function", "getPositions");
      execute(data, authUrl)
        .then((res) => {
          if (res.data.success) {
            this.positions = res.data.data.positions || [];
          }
        })
        .catch(() => {});
    },

    getPromotions() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getPromotions");
      data.append("startDate", this.filters.startDate);
      data.append("endDate", this.filters.endDate);
      data.append("page", this.filters.page);
      data.append("limit", this.filters.limit);

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }

      if (this.filters.promotionType) {
        data.append("promotionType", this.filters.promotionType);
      }

      if (this.filters.search) {
        data.append("search", this.filters.search);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.promotions = res.data.data.promotions || [];
              this.metrics = res.data.data.metrics || {};
              this.upcomingPromotions = res.data.data.upcomingPromotions || [];
              this.pagination = res.data.data.pagination || {
                currentPage: 1,
                totalPages: 1,
                totalPromotions: 0,
                limit: 10
              };
            } else {
              console.error("Error fetching promotions:", res.data.message);
              this.showToast("error", "Failed to load promotions data: " + res.data.message);
              this.promotions = [];
              this.metrics = {};
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching promotions");
            this.promotions = [];
            this.metrics = {};
          });
    },

    getEligibleEmployees() {
      const data = new FormData();
      data.append("function", "getEligibleEmployees");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.eligibleEmployees = res.data.data.eligibleEmployees || [];
            } else {
              console.error("Error fetching eligible employees:", res.data.message);
              this.showToast("error", "Failed to load eligible employees");
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching eligible employees");
          });
    },

    refreshData() {
      this.getPromotions();
    },

    resetFilters() {
      this.filters = {
        departmentId: "",
        promotionType: "",
        startDate: this.getDateOffset(-90),
        endDate: this.getDateOffset(90),
        search: "",
        page: 1,
        limit: 10
      };
      this.getPromotions();
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages || page === this.filters.page) {
        return;
      }

      this.filters.page = page;
      this.getPromotions();
    },

    getPageNumbers() {
      const totalPages = this.pagination.totalPages || 1;
      const currentPage = this.pagination.currentPage || 1;

      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      const pages = [1];

      if (currentPage <= 3) {
        pages.push(2, 3);
        if (totalPages > 3) pages.push('...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push('...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }

      return pages;
    },

    // Modal methods
    openPromotionModal() {
      this.showPromotionModal = true;
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
    },

    closePromotionModal() {
      this.showPromotionModal = false;
      document.body.style.overflow = '';
    },

    openDetailsModal() {
      this.showDetailsModal = true;
      document.body.style.overflow = 'hidden';
    },

    closeDetailsModal() {
      this.showDetailsModal = false;
      document.body.style.overflow = '';
    },

    openApprovalModal() {
      this.showApprovalModal = true;
      document.body.style.overflow = 'hidden';
    },

    closeApprovalModal() {
      this.showApprovalModal = false;
      document.body.style.overflow = '';
    },

    openRejectionModal() {
      this.showRejectionModal = true;
      document.body.style.overflow = 'hidden';
    },

    closeRejectionModal() {
      this.showRejectionModal = false;
      document.body.style.overflow = '';
    },

    openCompleteModal() {
      this.showCompleteModal = true;
      document.body.style.overflow = 'hidden';
    },

    closeCompleteModal() {
      this.showCompleteModal = false;
      document.body.style.overflow = '';
    },

    createPromotion() {
      this.isEditMode = false;
      this.promotionForm = {
        employeeId: "",
        previousDesignation: "",
        newDesignation: "",
        previousDepartmentName: "",
        newDepartmentId: "",
        promotionDate: new Date().toISOString().split('T')[0],
        effectiveDate: new Date().toISOString().split('T')[0],
        reason: "",
        recommendations: "",
        promotionType: "Regular",
        performanceRating: "",
        documentsList: []
      };

      this.selectedEmployee = null;
      this.removedDocuments = [];
      this.pendingFiles = [];

      this.getEligibleEmployees();
      this.openPromotionModal();
    },

    editPromotion(promotion) {
      this.isEditMode = true;
      this.selectedPromotion = promotion;

      this.promotionForm = {
        promotionId: promotion.id,
        employeeId: promotion.employeeId,
        previousDesignation: promotion.previousDesignation,
        newDesignation: promotion.newDesignation,
        previousDepartmentName: promotion.previousDepartmentName,
        newDepartmentId: promotion.newDepartmentId || promotion.previousDepartmentId,
        promotionDate: promotion.promotionDate,
        effectiveDate: promotion.effectiveDate,
        reason: promotion.reason,
        recommendations: promotion.recommendations,
        promotionType: promotion.promotionType,
        performanceRating: promotion.performanceRating,
        documentsList: promotion.documentsList || []
      };

      this.selectedEmployee = {
        id: promotion.employeeId,
        name: promotion.employeeName,
        designation: promotion.previousDesignation,
        departmentName: promotion.previousDepartmentName
      };

      this.removedDocuments = [];
      this.pendingFiles = [];

      this.openPromotionModal();
    },

    handleFileSelection(event) {
      this.pendingFiles = event.target.files;
    },

    removeDocument(doc, index) {
      this.removedDocuments.push(doc.path);
      this.promotionForm.documentsList.splice(index, 1);
    },

    async submitPromotion() {
      if (!this.validatePromotionForm()) {
        return;
      }

      const data = new FormData();

      if (this.isEditMode) {
        data.append("function", "updatePromotion");
        data.append("promotionId", this.promotionForm.promotionId);
      } else {
        data.append("function", "createPromotion");
      }

      data.append("employeeId", this.promotionForm.employeeId);
      data.append("previousDesignation", this.promotionForm.previousDesignation);
      data.append("newDesignation", this.promotionForm.newDesignation);
      data.append("previousDepartmentId", this.promotionForm.previousDepartmentId);
      data.append("newDepartmentId", this.promotionForm.newDepartmentId);
      data.append("promotionDate", this.promotionForm.promotionDate);
      data.append("effectiveDate", this.promotionForm.effectiveDate);
      data.append("reason", this.promotionForm.reason);
      data.append("recommendations", this.promotionForm.recommendations);
      data.append("promotionType", this.promotionForm.promotionType);
      data.append("performanceRating", this.promotionForm.performanceRating);

      if (this.pendingFiles.length > 0) {
        for (let i = 0; i < this.pendingFiles.length; i++) {
          data.append(`document_${i}`, this.pendingFiles[i]);
        }
      }

      if (this.isEditMode && this.removedDocuments.length > 0) {
        data.append("removedDocuments", JSON.stringify(this.removedDocuments));
      }

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.closePromotionModal();
              this.showToast("success", res.data.message);
              this.getPromotions();
            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when saving promotion");
          });
    },

    validatePromotionForm() {
      if (!this.promotionForm.employeeId) {
        this.showToast("error", "Please select an employee");
        return false;
      }

      if (!this.promotionForm.newDesignation) {
        this.showToast("error", "New designation is required");
        return false;
      }

      if (!this.promotionForm.reason) {
        this.showToast("error", "Reason for promotion is required");
        return false;
      }

      return true;
    },

    viewPromotionDetails(promotion) {
      this.selectedPromotion = null;
      this.getPromotionDetails(promotion.id);
    },

    approvePromotion(promotion) {
      this.selectedPromotion = promotion;
      this.approvalForm = {
        approvalLevel: "",
        comments: ""
      };

      this.getPromotionDetails(promotion.id, 'approve');
    },

    rejectPromotion(promotion) {
      this.selectedPromotion = promotion;
      this.rejectionForm = {
        rejectionReason: "",
        approvalLevel: ""
      };

      this.getPromotionDetails(promotion.id, 'reject');
    },

    completePromotion(promotion) {
      this.selectedPromotion = promotion;
      this.completionForm = {
        comments: ""
      };

      this.openCompleteModal();
    },

    getPromotionDetails(promotionId, nextAction = null) {
      const data = new FormData();
      data.append("function", "getPromotionDetails");
      data.append("promotionId", promotionId);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.selectedPromotion = res.data.data.selectedPromotion;

              if (this.selectedPromotion.approvals && this.selectedPromotion.approvals.length > 0) {
                const pendingApproval = this.selectedPromotion.approvals.find(a => a.status === 'Pending');
                if (pendingApproval) {
                  this.approvalForm.approvalLevel = pendingApproval.approvalLevel;
                }
              }

              if (nextAction === 'approve') {
                this.openApprovalModal();
              } else if (nextAction === 'reject') {
                this.openRejectionModal();
              } else {
                this.openDetailsModal();
              }
            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching promotion details");
          });
    },

    confirmApprovePromotion() {
      if (!this.approvalForm.approvalLevel) {
        this.showToast("error", "Please select an approval level");
        return;
      }

      const data = new FormData();
      data.append("function", "approvePromotion");
      data.append("promotionId", this.selectedPromotion.id);
      data.append("approvalLevel", this.approvalForm.approvalLevel);
      data.append("comments", this.approvalForm.comments);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.closeApprovalModal();
              this.showToast("success", res.data.message);
              this.getPromotions();
            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when approving promotion");
          });
    },

    confirmRejectPromotion() {
      if (!this.rejectionForm.rejectionReason) {
        this.showToast("error", "Please provide a reason for rejection");
        return;
      }

      const data = new FormData();
      data.append("function", "rejectPromotion");
      data.append("promotionId", this.selectedPromotion.id);
      data.append("rejectionReason", this.rejectionForm.rejectionReason);

      if (this.rejectionForm.approvalLevel) {
        data.append("approvalLevel", this.rejectionForm.approvalLevel);
      }

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.closeRejectionModal();
              this.showToast("success", res.data.message);
              this.getPromotions();
            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when rejecting promotion");
          });
    },

    confirmCompletePromotion() {
      const data = new FormData();
      data.append("function", "completePromotion");
      data.append("promotionId", this.selectedPromotion.id);
      data.append("comments", this.completionForm.comments);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.closeCompleteModal();
              this.showToast("success", res.data.message);
              this.getPromotions();
            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when completing promotion");
          });
    },

    exportPromotions() {
      this.exportPromotionsCsv();
    },
    promotionExportHeaders() {
      return [
        "Employee",
        "Type",
        "From",
        "To",
        "Department",
        "Effective",
        "Status",
      ];
    },
    promotionExportRows(list) {
      return (list || this.promotions || []).map((p) => [
        p.employeeName || p.employee_name || "",
        p.promotionType || p.promotion_type || "",
        p.currentDesignation || p.current_designation || "",
        p.newDesignation || p.new_designation || "",
        p.departmentName || p.department_name || "",
        p.promotionDate || p.effectiveDate || p.effective_date || "",
        p.status || "",
      ]);
    },
    async exportPromotionsCsv() {
      try {
        downloadCsv(
          this.promotionExportHeaders(),
          this.promotionExportRows(),
          `promotions_${Date.now()}.csv`
        );
        this.showToast && this.showToast("success", "CSV downloaded");
        toast.success("Promotions CSV downloaded");
      } catch (e) {
        toast.error("CSV export failed");
      }
    },
    async exportPromotionsPdf() {
      try {
        await downloadCountyTablePdf({
          title: "EMPLOYEE PROMOTIONS REPORT",
          subtitle: "HR Actions · Promotions",
          periodLabel:
            periodLabelFromFilters({
              startDate: this.filters?.startDate,
              endDate: this.filters?.endDate,
            }) || new Date().toLocaleString("en-KE"),
          headers: this.promotionExportHeaders(),
          rows: this.promotionExportRows(),
          filename: `promotions_${Date.now()}.pdf`,
          summaryRows: [
            ["Records", String((this.promotions || []).length)],
            ["Promotion type", this.filters?.promotionType || "All"],
            ["Search", this.filters?.search || "—"],
          ],
        });
        toast.success("Promotions PDF downloaded");
      } catch (e) {
        toast.error("PDF export failed");
      }
    },

    // Utility methods
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'N/A';
      const date = new Date(dateTimeString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatDateMonth(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short' });
    },

    formatDateDay(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.getDate();
    },

    getPromotionTypeColor(type) {
      if (!type) return 'bg-secondary';
      const typeMap = {
        'Regular': 'bg-primary',
        'Performance-Based': 'bg-success',
        'Probation Completion': 'bg-info',
        'Exceptional': 'bg-warning',
        'Internal Transfer': 'bg-purple',
        'Restructuring': 'bg-indigo',
        'Project Completion': 'bg-teal',
        'Skills Upgrade': 'bg-pink',
        'Other': 'bg-secondary'
      };
      return typeMap[type] || 'bg-secondary';
    },

    getPromotionTypePercentage(type) {
      if (!this.metrics.typeCounts || !this.metrics.totalPromotions) return 0;
      const count = this.metrics.typeCounts[type] || 0;
      const total = Object.values(this.metrics.typeCounts).reduce((sum, count) => sum + count, 0);
      return ((count / total) * 100).toFixed(1);
    },

    getStatusBadgeClass(status) {
      if (!status) return 'bg-secondary';
      const statusMap = {
        'Pending': 'bg-warning',
        'Approved': 'bg-success',
        'Rejected': 'bg-danger',
        'Completed': 'bg-info'
      };
      return statusMap[status] || 'bg-secondary';
    },

    getPerformanceRatingColor(rating) {
      if (!rating) return 'bg-secondary';
      if (rating >= 4) return 'bg-success';
      if (rating >= 3) return 'bg-info';
      if (rating >= 2) return 'bg-warning';
      return 'bg-danger';
    },

    getApprovalStatusClass(status, isConnector = false) {
      if (!status) return isConnector ? 'approval-connector-pending' : 'bg-secondary text-white';
      const statusMap = {
        'Pending': isConnector ? 'approval-connector-pending' : 'bg-warning text-white',
        'Approved': isConnector ? 'approval-connector-approved' : 'bg-success text-white',
        'Rejected': isConnector ? 'approval-connector-rejected' : 'bg-danger text-white'
      };
      return statusMap[status] || (isConnector ? 'approval-connector-pending' : 'bg-secondary text-white');
    },

    getApprovalStatusIcon(status) {
      if (!status) return 'mdi-help-circle';
      const statusMap = {
        'Pending': 'mdi-clock-outline',
        'Approved': 'mdi-check-circle',
        'Rejected': 'mdi-close-circle'
      };
      return statusMap[status] || 'mdi-help-circle';
    },

    getActivityTypeClass(type) {
      if (!type) return 'bg-secondary text-white';
      const typeMap = {
        'create': 'bg-primary text-white',
        'update': 'bg-info text-white',
        'approve': 'bg-success text-white',
        'reject': 'bg-danger text-white',
        'complete': 'bg-purple text-white'
      };
      return typeMap[type] || 'bg-secondary text-white';
    },

    getActivityTypeIcon(type) {
      if (!type) return 'mdi-help-circle';
      const typeMap = {
        'create': 'mdi-plus-circle',
        'update': 'mdi-pencil',
        'approve': 'mdi-check-circle',
        'reject': 'mdi-close-circle',
        'complete': 'mdi-check-all'
      };
      return typeMap[type] || 'mdi-help-circle';
    },

    canApprove(promotion) {
      return promotion.status === 'Pending' || promotion.status === 'Approved';
    },

    calculateSalaryIncrease() {
      return 0;
    },

    calculateSalaryIncreaseFromValues(previous, current) {
      if (!previous || !current) {
        return 0;
      }

      const previousSalary = parseFloat(previous);
      const newSalary = parseFloat(current);

      if (isNaN(previousSalary) || isNaN(newSalary) || previousSalary === 0) {
        return 0;
      }

      return ((newSalary - previousSalary) / previousSalary * 100).toFixed(2);
    },

    calculateServiceYears(hireDate) {
      if (!hireDate) return 0;
      const hireDateTime = new Date(hireDate).getTime();
      const now = new Date().getTime();
      const diffInYears = (now - hireDateTime) / (1000 * 60 * 60 * 24 * 365.25);
      return Math.floor(diffInYears);
    },

    showToast(type, message) {
      if (type === 'success') {
        toast.success(message);
      } else if (type === 'error') {
        toast.error(message);
      } else if (type === 'warning') {
        toast.warning(message);
      } else {
        toast.info(message);
      }
    },

    approvePromotionFromDetails() {
      this.approvalForm = {
        approvalLevel: "",
        comments: ""
      };

      if (this.selectedPromotion.approvals && this.selectedPromotion.approvals.length > 0) {
        const pendingApproval = this.selectedPromotion.approvals.find(a => a.status === 'Pending');
        if (pendingApproval) {
          this.approvalForm.approvalLevel = pendingApproval.approvalLevel;
        }
      }

      this.closeDetailsModal();
      setTimeout(() => {
        this.openApprovalModal();
      }, 300);
    },

    rejectPromotionFromDetails() {
      this.rejectionForm = {
        rejectionReason: "",
        approvalLevel: ""
      };

      this.closeDetailsModal();
      setTimeout(() => {
        this.openRejectionModal();
      }, 300);
    },

    completePromotionFromDetails() {
      this.completionForm = {
        comments: ""
      };

      this.closeDetailsModal();
      setTimeout(() => {
        this.openCompleteModal();
      }, 300);
    },

    toggleSalaryVisibility(salaryId) {
      const id = salaryId || `salary-${Date.now()}`;

      this.revealedSalaries = {
        ...this.revealedSalaries,
        [id]: !this.revealedSalaries[id]
      };

      if (this.revealedSalaries[id]) {
        setTimeout(() => {
          this.revealedSalaries = {
            ...this.revealedSalaries,
            [id]: false
          };
        }, this.revealDuration);
      }
    },

    isSalaryRevealed(salaryId) {
      return !!this.revealedSalaries[salaryId];
    },

    formatSalary(salary, salaryId) {
      if (!salary) return 'N/A';

      if (this.revealedSalaries[salaryId] === undefined) {
        this.revealedSalaries = {
          ...this.revealedSalaries,
          [salaryId]: false
        };
      }

      const formattedValue = typeof salary === 'number'
          ? salary.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
          : salary;

      return formattedValue;
    }
  },
  watch: {
    'promotionForm.employeeId': {
      handler(employeeId) {
        if (!employeeId) {
          this.selectedEmployee = null;
          return;
        }

        const employee = this.eligibleEmployees.find(emp => emp.id === employeeId);
        if (employee) {
          this.selectedEmployee = employee;

          this.promotionForm.previousDesignation = employee.designation;
          this.promotionForm.previousDepartmentId = employee.departmentID;
          this.promotionForm.previousDepartmentName = employee.departmentName;
          this.promotionForm.newDepartmentId = employee.departmentID;
          this.promotionForm.previousSalary = employee.salary;

          const currentSalary = parseFloat(employee.salary);
          if (!isNaN(currentSalary)) {
            this.promotionForm.newSalary = Math.round(currentSalary * 1.1);
          }
        }
      },
      immediate: true
    },
    'filters.departmentId': function() {
      this.filters.page = 1;
      this.getPromotions();
    },
    'filters.promotionType': function() {
      this.filters.page = 1;
      this.getPromotions();
    },
    'filters.search': _.debounce(function() {
      this.filters.page = 1;
      this.getPromotions();
    }, 500),
    'filters.startDate': function() {
      this.getPromotions();
    },
    'filters.endDate': function() {
      this.getPromotions();
    }
  }
};
</script>

<style scoped>
/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  overflow-y: auto;
  padding: 20px;
}

.modal-container {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
  width: 100%;
  max-width: 500px;
  margin: 20px auto;
}

.modal-container.modal-lg {
  max-width: 800px;
}

.modal-content {
  border: none;
  border-radius: 8px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
}

.modal-body {
  padding: 1rem;
  max-height: 70vh;
  overflow-y: auto;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 1rem;
  border-top: 1px solid #dee2e6;
  gap: 8px;
}

.btn-close {
  padding: 0.5rem;
  background: transparent url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23000'%3e%3cpath d='M.293.293a1 1 0 011.414 0L8 6.586 14.293.293a1 1 0 111.414 1.414L9.414 8l6.293 6.293a1 1 0 01-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 01-1.414-1.414L6.586 8 .293 1.707a1 1 0 010-1.414z'/%3e%3c/svg%3e") center/1em auto no-repeat;
  border: 0;
  border-radius: 4px;
  opacity: 0.5;
  cursor: pointer;
}

.btn-close:hover {
  opacity: 0.75;
}

/* Salary blur styles */
.salary-value {
  position: relative;
  cursor: pointer;
  display: inline-block;
  user-select: none;
}

.salary-value.blurred {
  filter: blur(5px);
  -webkit-filter: blur(5px);
}

.salary-value.blurred:hover {
  filter: blur(4px);
  -webkit-filter: blur(4px);
}

.salary-value::after {
  content: "Click to reveal";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  color: transparent;
  pointer-events: none;
  transition: color 0.2s ease;
}

.salary-value.blurred::after {
  color: #495057;
}

.salary-value.revealed {
  animation: reveal 0.3s ease-out;
}

@keyframes reveal {
  0% {
    filter: blur(5px);
    -webkit-filter: blur(5px);
  }
  100% {
    filter: blur(0);
    -webkit-filter: blur(0);
  }
}

/* Approval connector styles */
.approval-connector {
  width: 2px;
  height: 40px;
  margin-left: 19px;
  margin-top: 5px;
  margin-bottom: 5px;
}

.approval-connector-pending {
  background-color: #ffc107;
}

.approval-connector-approved {
  background-color: #198754;
}

.approval-connector-rejected {
  background-color: #dc3545;
}

/* Promotion Type Colors */
.bg-purple {
  background-color: #6f42c1;
  color: white;
}

.bg-indigo {
  background-color: #6610f2;
  color: white;
}

.bg-teal {
  background-color: #20c997;
  color: white;
}

.bg-pink {
  background-color: #d63384;
  color: white;
}

/* Card hover effects */
.card {
  transition: transform 0.3s, box-shadow 0.3s;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

/* Custom table styles */
.table th {
  font-weight: 600;
  color: #495057;
}

.table td {
  vertical-align: middle;
}

.promotion-date-badge {
  min-width: 45px;
}

.date-month {
  text-transform: uppercase;
  font-size: 0.8rem;
  color: #6c757d;
}

/* Prevent body scroll when modal is open */
body.modal-open {
  overflow: hidden;
}
</style>